import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated } from '../services/authService';
import Header from './Header';
import './EventDetails.css';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { useTheme } from '@mui/material/styles'; // Import useTheme
import EventDetailsCard from './EventDetailsCard';
import { useTenant } from '../contexts/TenantContext';
import { useEvents } from '../contexts/EventContext';
import EventCommentList from './EventCommentList';
import { getEventComments, getEventDetails } from '../services/businessService';

const EventDetailsV2 = () => {
  const { t } = useTranslation(); // Call useTranslation hook
  const theme = useTheme(); // Initialize theme
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const { tenant_id, event_id } = useParams(); // Extract tenant_id, event_id, and task_id from URL
  const navigate = useNavigate();
  const initialLoadPerformed = useRef(false); // Add this line
  const { selectedTenantId, tenantData } = useTenant();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const { fetchUserEvent } = useEvents();

  const labels = {
    1: t('eventCard.rating.useless'),
    2: t('eventCard.rating.poor'),
    3: t('eventCard.rating.ok'),
    4: t('eventCard.rating.good'),
    5: t('eventCard.rating.excellent'),
  };

  function getLabelText(value) {
    const label = labels[value];
    return `${value} ${t('eventCard.rating.banana', { count: value })}${label ? `, ${label}` : ''}`;
  }

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        // Vérifiez si l'utilisateur est authentifié
        const authenticated = isAuthenticated();
        if (!authenticated) {
          navigate(`/app/${tenant_id}/login`, { replace: true }); // Use replace to avoid adding to history
          return;
        }

        // Chargez les détails de l'événement
        const eventFromAPI = await fetchUserEvent(event_id);
        if (!eventFromAPI) {
          throw new Error('Event not found');
        }
        setEvent(eventFromAPI);
      } catch (error) {
        console.error('Error fetching event details', error);
        setError('Failed to load event details. Please try again later.');
        if (error.response && error.response.status === 401) {
          navigate('/login', { replace: true }); // Use replace to avoid adding to history
        }
      } finally {
        setLoading(false);
      }
    };

    if (!initialLoadPerformed.current) {
      fetchEventDetails();
      initialLoadPerformed.current = true;
    }
  }, [tenant_id, event_id, navigate, selectedTenantId]);

  // Déplacez fetchComments hors du useEffect pour qu'il soit accessible partout
  const fetchComments = async () => {
    if (!selectedTenantId || !event_id) return;

    getEventComments(selectedTenantId, event_id).then(data => {
        setComments(data.results || []);
        setLoadingComments(false);
    }).catch(error => {
        setComments([]);
        setLoadingComments(false);
    });

    // try {
    //   setLoadingComments(true);
    //   const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    //   const response = await axios.get(
    //     `${BACKEND_URL}/app/t:${selectedTenantId}/api/v2/events/e:${event_id}/comments/`,
    //     {
    //       headers: {
    //         Authorization: `Bearer ${localStorage.getItem('access_token')}`
    //       }
    //     }
    //   );

    //   // Correction : support du format paginé { count, results }
    //   if (Array.isArray(response.data)) {
    //     setComments(response.data);
    //   } else if (response.data && Array.isArray(response.data.results)) {
    //     setComments(response.data.results);
    //   } else {
    //     setComments([]);
    //   }
    // } catch (error) {
    //   setComments([]);
    // } finally {
    //   setLoadingComments(false);
    // }
  };

  // Pour éviter le double appel, ne lance fetchComments qu'une seule fois au premier chargement
  const commentsLoaded = useRef(false);
  useEffect(() => {
    if (!commentsLoaded.current && selectedTenantId && event_id) {
      fetchComments();
      commentsLoaded.current = true;
    }
  }, [selectedTenantId, event_id]);

  const handleBack = () => {
    navigate(`/app/${tenantData.subdomain_prefix}/dashboard`); // Navigate back to tenant-specific dashboard
  };

  // Handle adaptive card actions
  const onActionSubmit = (actionData) => {
    console.log('Card action submitted:', actionData);
    if (actionData.data.action === 'join') {
      alert(t('eventDetails.joinedEvent', { title: event.title })); // Translate alert message
    } else if (actionData.data.action === 'cancel') {
      alert(t('eventDetails.registrationCancelled')); // Translate alert message
    } else if (actionData.data.action === 'contact') {
      alert(t('eventDetails.contactingOrganizer')); // Translate alert message
    }
  };

  // Ajoute une fonction pour rafraîchir les commentaires après ajout
  const refreshComments = () => {
    fetchComments();
  };

  useEffect(() => {
    // Au chargement du composant, on s'assure que le dashboard est dans l'historique
    // en ajoutant une entrée explicite si ce n'est pas le cas
    const currentPath = window.location.pathname;
    const dashboardUrl = tenantData?.subdomain_prefix 
      ? `/app/${tenantData.subdomain_prefix}/dashboard`
      : `/app/${tenant_id}/dashboard`;

    // Si on n'arrive pas du dashboard et que l'historique ne contient qu'une entrée,
    // on force l'ajout du dashboard dans l'historique
    if (window.history.length <= 1 || document.referrer === '' || document.referrer.includes('localhost:3000/')) {
      console.log("[HISTORY] Ajout du dashboard dans l'historique");
      window.history.replaceState(null, '', dashboardUrl);
      window.history.pushState(null, '', currentPath);
    }
  }, [tenantData?.subdomain_prefix, tenant_id]);

  // if (loading) {
  //   return <div className="loading">{t('eventDetails.loading')}</div>; // Translate loading message
  // }

  if (error) {
    return <div className="error">{error}</div>; // Error message is already dynamic
  }

  return (
    <div>
      {/* <Header title={t('eventDetails.pageTitle')} /> */}

      <div className="event-details-container">
        <button 
          onClick={handleBack} 
          className="back-button" 
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          style={{
            color: isButtonHovered ? 'white' : theme.palette.primary.main,
            backgroundColor: isButtonHovered ? theme.palette.primary.main : 'transparent',
            borderColor: theme.palette.primary.main
          }}
        >
          {t('eventDetails.backToDashboard')}
        </button> {/* Translate back button */}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <img
            src="/logo_banane_transparent.png"
            alt="Logo"
            style={{
              width: '150px',
              marginBottom: '20px',
              animation: 'spin 2s linear infinite' // Add spinning animation
            }}
          />
          <p>{t('login.processing')}</p>
          <style>
            {`
              @keyframes spin {
                from {
                  transform: rotate(0deg);
                }
                to {
                  transform: rotate(360deg);
                }
              }
            `}
          </style>
        </div>
        ) : event ? (
          <>
            <div style={{ marginTop: 32, width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
              <EventDetailsCard event={event} formatDate={date => date ? new Date(date).toLocaleString() : ''} onCommentAdded={refreshComments} />
            </div>
            {/* Liste des commentaires seulement si l'evenement est passé (à eu lieu) */}
            <>
              <EventCommentList event={event} comments={comments} />
            </>
        </>
        ) : (
          <p>{t('eventDetails.eventNotFound')}</p> // Translate event not found message
        )}
      </div>
    </div>
  );
};

export default EventDetailsV2;
